import { useSyncExternalStore } from 'react';

import { defaultTextScale, isTextScale, type TextScale } from '@core/session/textScale';

/**
 * How large the product draws itself, resolved per person and then per company.
 *
 * <p>
 * Reported from use: the theme reads as corporate and the type is too small for some customers.
 * Both come from one cause — the product was scaled for density, the way an accounting package is.
 * That is right for a screen an analyst stares at all day and wrong for a counter, where the person
 * is standing, half-turned to a customer, and may be fifty.
 * </p>
 *
 * <p>
 * <strong>The control height matters as much as the type.</strong> 34px fails the 44px touch target
 * phones and tablets are designed around, and a counter tablet is exactly where this runs — so the
 * old scale was not only hard to read, it was hard to hit.
 * </p>
 *
 * <p>
 * <strong>`comfortable` is the new default, so this is a visible change for everybody</strong> —
 * deliberately, since that is the complaint. `compact` is exactly what the product was before, for
 * anyone who wants the density back, and nobody is stranded by the new baseline.
 * </p>
 *
 * <p>
 * Three answers, in this order: what this person chose on this device, what their company chose,
 * and the built-in default. The shape is `format/datePreference.ts` — an external store rather than
 * context, because the values are read by `AppProviders` and by CSS custom properties on the root
 * element, neither of which is a component tree worth threading a provider through. Read that file
 * before changing the mechanics here; the reasoning is the same and is written out there.
 * </p>
 */
// The names, the options and the rule moved to the shared core with the phone's Appearance screen
// (docs/Mobile_Redesign_Plan.md, step 11); re-exported so this module's importers stay put.
export {
  defaultTextScale,
  isTextScale,
  textScaleOptions,
  type TextScale,
} from '@core/session/textScale';

/**
 * What each step actually sets.
 *
 * <p>
 * `fontSize` and `controlHeight` are Ant Design tokens and reach it through `AppProviders`. The
 * rest are CSS custom properties consumed by AttireRent's own additions at the end of `style.css`,
 * because the template styles its own markup and antd never sees those elements — a badge, a small
 * button, a field hint and a page subtitle are all the template's.
 * </p>
 *
 * <p>
 * <strong>`compact` is the product as it stood before this existed</strong>, and its numbers are
 * the ones the old `theme.ts` carried. Keeping them exact is what makes the setting reversible
 * rather than approximately reversible.
 * </p>
 */
export interface TextScaleTokens {
  /** antd `fontSize` — every table cell, select and date picker. */
  fontSize: number;
  /** antd `controlHeight` — every input and picker. 44px is the touch target to clear. */
  controlHeight: number;
  /** `--scale-btn-sm`, the template's small button. */
  buttonSmall: number;
  /** `--scale-badge`, `StatusBadge`. */
  badge: number;
  /** `--scale-field-hint`, the hint inside `Field`'s label. */
  fieldHint: number;
  /** `--scale-page-subtitle`, the line under a `PageHeading`. */
  pageSubtitle: number;
  /**
   * `--scale-list-name`, the name on a dashboard list row and the label over a stat card's figure.
   *
   * <p>
   * <strong>These last two exist because the setting did nothing on the morning screen.</strong>
   * It is built from the template's own markup, whose type sizes are Tailwind literals —
   * `text-xs`, `text-[11px]` — and a literal consumes no token and no custom property. So changing
   * the scale moved that screen's badges, its "View all" button and its subtitle, and left every
   * word of the content it is actually read for exactly where it was. Reported as "font size
   * change not working", which is precisely what it looked like.
   * </p>
   */
  listName: number;
  /** `--scale-list-meta`, the muted reference under a name and the label under a trailing figure. */
  listMeta: number;
}

export const textScales: Readonly<Record<TextScale, TextScaleTokens>> = {
  compact: {
    fontSize: 13,
    controlHeight: 34,
    buttonSmall: 12,
    badge: 11,
    fieldHint: 11.5,
    pageSubtitle: 13,
    // The literals the dashboard was written with — `text-xs` and `text-[11px]`. Compact reproduces
    // the product as it stood, here as everywhere else in this table.
    listName: 12,
    listMeta: 11,
  },
  comfortable: {
    fontSize: 15,
    controlHeight: 42,
    buttonSmall: 14,
    badge: 12.5,
    fieldHint: 13,
    pageSubtitle: 15,
    listName: 13.5,
    listMeta: 12.5,
  },
  large: {
    fontSize: 17,
    controlHeight: 48,
    buttonSmall: 15,
    badge: 13.5,
    fieldHint: 14,
    pageSubtitle: 17,
    listName: 15,
    listMeta: 14,
  },
};

/** What a shop that never opens the settings screen gets — and the answer to the reported complaint. */
/** The three steps with the words a person chooses between, for the settings screen and account menu. */
/**
 * Whether a value is a step this product knows how to draw.
 *
 * <p>
 * Everything read back from storage or from the session goes through this, for the reason
 * `isKnownDateFormat` exists: a hand-edited storage entry or a step since retired would otherwise
 * index the table with a key it does not hold, and every size in the product would come out
 * `undefined`.
 * </p>
 */
const USER_KEY = 'attirerent.textScale';
const COMPANY_KEY = 'attirerent.textScale.company';

const listeners = new Set<() => void>();

function readStored(key: string): TextScale | null {
  try {
    const stored = localStorage.getItem(key);

    return isTextScale(stored) ? stored : null;
  } catch {
    // Private mode, or storage disabled. Falling back is correct and silent — the product still
    // draws itself, at the size below whichever one could not be read.
    return null;
  }
}

let userScale: TextScale | null = readStored(USER_KEY);
let companyScale: TextScale | null = readStored(COMPANY_KEY);

function writeStored(key: string, value: TextScale | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // The choice still applies to this sitting; it simply does not survive a reload, which is a
    // better outcome than refusing to change the size at all.
  }
}

function announce(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** The scale in force: this person's choice, else their company's, else the default. */
export function resolveTextScale(): TextScale {
  return userScale ?? companyScale ?? defaultTextScale;
}

/** The numbers in force. */
export function resolveTextScaleTokens(): TextScaleTokens {
  return textScales[resolveTextScale()];
}

/** What this person has chosen for this device, or null when they are following the company. */
export function userTextScale(): TextScale | null {
  return userScale;
}

/** What the company has chosen, or null when it has not. */
export function companyTextScale(): TextScale | null {
  return companyScale;
}

/**
 * Writes the CSS half onto the root element.
 *
 * <p>
 * The antd half is a re-render; these four are custom properties, which no amount of re-rendering
 * reaches because the elements that consume them are the template's own markup styled from
 * `style.css`. Called from the same effect that pushes the scale into Ant Design, so the two halves
 * of one setting can never be a step apart.
 * </p>
 */
export function applyTextScale(scale: TextScale = resolveTextScale()): void {
  const tokens = textScales[scale];
  const root = document.documentElement;

  /*
    The antd base size, published as a custom property as well as a token.

    `theme-antd.css` — the template's own antd overrides, vendored — pins `.ant-table-content table`
    to `var(--text-sm)` with `!important`, and the date picker's input and panel to a literal 14px.
    Those rules are unlayered, so they beat the token whatever it says: **every list screen and every
    date picker in the product ignored `fontSize` entirely**, before this setting existed as much as
    after. The override at the end of `style.css` needs a value to point at, and taking it from
    `fontSize` rather than declaring a sixth number is what stops the table drifting away from the
    control beside it.
  */
  root.style.setProperty('--scale-font-size', `${tokens.fontSize}px`);

  root.style.setProperty('--scale-btn-sm', `${tokens.buttonSmall}px`);
  root.style.setProperty('--scale-badge', `${tokens.badge}px`);
  root.style.setProperty('--scale-field-hint', `${tokens.fieldHint}px`);
  root.style.setProperty('--scale-page-subtitle', `${tokens.pageSubtitle}px`);
  root.style.setProperty('--scale-list-name', `${tokens.listName}px`);
  root.style.setProperty('--scale-list-meta', `${tokens.listMeta}px`);

  // Named so a stylesheet or a screenshot can tell which step is in force without reading four
  // lengths back and matching them against the table above.
  root.setAttribute('data-text-scale', scale);
}

/**
 * Sets this device's override, or clears it with null so the company's choice applies again.
 *
 * Anything unrecognised clears rather than being stored, so a stale value from an older build
 * cannot strand somebody at a size the product no longer draws.
 */
export function setUserTextScale(scale: TextScale | null): void {
  const next = isTextScale(scale) ? scale : null;

  if (next === userScale) {
    return;
  }

  userScale = next;
  writeStored(USER_KEY, next);
  applyTextScale();
  announce();
}

/**
 * Records the company's choice, from the session.
 *
 * Called by `App` whenever the signed-in user changes, including with null — a company that has
 * cleared its setting must fall back to the default rather than keep the last one this device
 * happened to see cached.
 */
export function setCompanyTextScale(scale: string | null): void {
  const next = isTextScale(scale) ? scale : null;

  if (next === companyScale) {
    return;
  }

  companyScale = next;
  writeStored(COMPANY_KEY, next);
  applyTextScale();
  announce();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/**
 * Subscribes to the scale in force.
 *
 * <p>
 * `AppProviders` calls this, and that one call is what makes a change repaint Ant Design — its
 * tokens are resolved in JavaScript at render time, so without a re-render the page would change
 * size around a table that stayed put. That is the defect `useThemeMode` exists to prevent, in the
 * same place and for the same reason.
 * </p>
 */
export function useTextScale(): TextScale {
  return useSyncExternalStore(subscribe, resolveTextScale, resolveTextScale);
}

/*
  Applied at module load, not from an effect.

  `userScale` and `companyScale` are read from storage above, so the right answer is already known
  before the first paint. Waiting for an effect would draw one frame at the CSS default and then
  correct it — which is precisely the flicker `format/datePreference.ts` caches the company's format
  to avoid, and is worse here because it moves every control on the page.

  Guarded because the test suite's default environment is `node`, where there is no document. The
  store itself is testable without one; only this line needs the browser.
*/
if (typeof document !== 'undefined') {
  applyTextScale();
}
